// 📁 src/admin/Products.js (fixed top-to-bottom: uses Render backend for ALL requests)
import React, { useEffect, useState } from "react";
import { api } from "../axiosClient";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("All");
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    price: "",
    category: "Raw",
    description: "",
    inStock: true,
    visible: true,
    image: "",
    imageFile: null,
  });

  // ✅ Fetch products (ALWAYS from backend)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/api/products", {
          headers: { "Cache-Control": "no-cache" },
        });
        setProducts(res.data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      }
    };

    fetchProducts();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (type === "file") {
      const file = files?.[0] || null;
      setForm((prev) => ({ ...prev, imageFile: file }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const refreshProducts = async () => {
    const res = await api.get("/api/products", {
      headers: { "Cache-Control": "no-cache" },
    });
    setProducts(res.data);
  };

  const uploadImageIfAny = async () => {
  if (!form.imageFile) return "";

  const token = localStorage.getItem("token");   // ✅ get JWT

  const data = new FormData();
  data.append("image", form.imageFile);

  const res = await api.post("/api/upload/product",data,{
headers:{
"Content-Type":"multipart/form-data"
}
},
    {
      headers: {
        Authorization: `Bearer ${token}`,   // ✅ THIS WAS MISSING
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return res.data?.imagePath || "";
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // ✅ image path logic
      let imagePath = "";

      if (form.imageFile) {
        imagePath = await uploadImageIfAny();
        if (!imagePath) {
          throw new Error("Image upload failed: imagePath missing");
        }
      } else if (editId && form.image && !form.image.startsWith("blob:")) {
        imagePath = form.image; // keep old image if editing and no new file selected
      }

      const productData = {
        name: form.name,
        price: Number(form.price), // safer than parseInt
        category: form.category,
        description: form.description,
        inStock: form.inStock,
        visible: form.visible,
        image: imagePath,
      };

      if (editId) {
        await api.put(`/api/products/${editId}`, productData);
      } else {
        await api.post("/api/products", productData);
      }

      await refreshProducts();

      // Reset state
      setShowModal(false);
      setEditId(null);
      setForm({
        name: "",
        price: "",
        category: "Raw",
        description: "",
        inStock: true,
        visible: true,
        image: "",
        imageFile: null,
      });
    } catch (err) {
      console.error("Error saving product:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    try {
      await api.delete(`/api/products/${id}`);
      setProducts((prev) => prev.filter((p) => p._id !== id));
    } catch (err) {
      console.error("Error deleting product:", err);
    }
  };

  const toggleVisibility = async (id, current) => {
    try {
      await api.patch(`/api/products/${id}/visibility`);
      setProducts((prev) =>
        prev.map((p) => (p._id === id ? { ...p, visible: !current } : p))
      );
    } catch (err) {
      console.error("Error toggling visibility:", err);
    }
  };

  const handleEdit = (product) => {
    setForm({
      name: product.name || "",
      price: product.price ?? "",
      category: product.category || "Raw",
      description: product.description || "",
      inStock: !!product.inStock,
      visible: !!product.visible,
      image: product.image || "",
      imageFile: null,
    });

    setEditId(product._id);
    setShowModal(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">Manage Products</h1>

        <button
          onClick={() => {
            setForm({
              name: "",
              price: "",
              category: "Raw",
              description: "",
              inStock: true,
              visible: true,
              image: "",
              imageFile: null,
            });
            setEditId(null);
            setShowModal(true);
          }}
          className="bg-[#105132] text-white px-4 py-2 rounded hover:bg-[#236e47]"
        >
          + Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-[#105132]">
          Filter by Category:
        </label>
        <select
          className="border p-2 rounded"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Raw">Raw</option>
          <option value="Roasted">Roasted</option>
          <option value="Flavored">Flavored</option>
          <option value="Gift Pack">Gift Pack</option>
        </select>
      </div>

      {/* Products Table */}
      <table className="min-w-full bg-white border rounded shadow text-sm">
        <thead className="bg-[#105132] text-white">
          <tr>
            <th className="p-2 text-left">Name</th>
            <th className="p-2">Category</th>
            <th className="p-2">Price</th>
            <th className="p-2">Stock</th>
            <th className="p-2">Visible</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>

        <tbody>
          {products
            .filter((p) => filter === "All" || p.category === filter)
            .map((p) => (
              <tr key={p._id} className="border-t hover:bg-[#f3f0e4]">
                <td className="p-2">{p.name}</td>
                <td className="p-2 text-center">{p.category}</td>
                <td className="p-2 text-center">₹{p.price}</td>
                <td className="p-2 text-center">
                  {p.inStock ? "In Stock" : "Out of Stock"}
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => toggleVisibility(p._id, p.visible)}
                    className={`text-xs px-2 py-1 rounded-full ${
                      p.visible
                        ? "bg-green-200 text-green-800"
                        : "bg-red-200 text-red-700"
                    }`}
                  >
                    {p.visible ? "Visible" : "Hidden"}
                  </button>
                </td>
                <td className="p-2 text-center space-x-2">
                  <button className="text-blue-600" onClick={() => handleEdit(p)}>
                    Edit
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDelete(p._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">
              {editId ? "Edit Product" : "Add Product"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Product Name"
                value={form.name}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
              />

              <input
                type="number"
                name="price"
                placeholder="Price"
                value={form.price}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
              />

              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              >
                <option>Raw</option>
                <option>Roasted</option>
                <option>Flavored</option>
                <option>Gift Pack</option>
              </select>

              <textarea
                name="description"
                placeholder="Description"
                value={form.description}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              />

              <input
                type="file"
                name="image"
                onChange={handleChange}
                className="w-full border p-2 rounded"
                accept="image/*"
              />

              {form.imageFile && (
                <div className="text-sm text-gray-600">
                  <p className="mb-1">Image Preview:</p>
                  <img
                    src={URL.createObjectURL(form.imageFile)}
                    alt="preview"
                    className="h-32 object-contain border rounded"
                  />
                </div>
              )}

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="inStock"
                  checked={form.inStock}
                  onChange={handleChange}
                />
                In Stock
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="visible"
                  checked={form.visible}
                  onChange={handleChange}
                />
                Show on Website
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditId(null);
                  }}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-[#105132] text-white rounded hover:bg-[#236e47]"
                >
                  {uploading ? "Saving..." : editId ? "Update Product" : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
